export enum RequestService {
	Open = "Open", // The ticket has been created and is awaiting review or assignment.
	InProgress = "In Progress", // The ticket is being actively worked on by a technician or support agent.
	Pending = "Pending", // The ticket is waiting for further information from the requester, or there’s a dependency (e.g., awaiting customer response, third-party vendor action).
	Escalated = "Escalated", // The ticket has been escalated to a higher tier of support due to its complexity or urgency.
	Resolved = "Resolved", // The issue has been addressed, and a solution has been implemented.
	Closed = "Closed", // The ticket is officially closed, often after confirmation that the requester is satisfied with the resolution.
	Reopened = "Reopened", // The ticket was previously closed or resolved, but the requester has indicated that the issue persists.
	Canceled = "Canceled" // The ticket was canceled, either by the requester or by the system due to inactivity or irrelevance.
}

// Possible values for the Product Types
export enum ProductType {
	ACCESS_CONTROL = "Access Control",
	AUDIO_VIDEO = "Audio & Video",
	SECURITY = "Security",
	GATE_CONTROLS = "Gate Controls",
	SURVEILLANCE = "Surveillance",
	ANTENNA_SYSTEMS = "Antenna Systems"
}

export enum AttachmentType {
	MANUAL = "MANUAL",
	VIDEO = "VIDEO",
	GENERAL_DOCUMENT = "GENERAL_DOCUMENT",
	TECHNICAL_DOCUMENT = "TECHNICAL_DOCUMENT",
	DIAGRAM = "DIAGRAM",
	PHOTO = "PHOTO",
	AUDIO = "AUDIO",
	GUIDE = "GUIDE"
}

export const AttachmentTypeDisplayNames: Record<AttachmentType, string> = {
	[AttachmentType.MANUAL]: "Manual",
	[AttachmentType.VIDEO]: "Video",
	[AttachmentType.GENERAL_DOCUMENT]: "General Document",
	[AttachmentType.TECHNICAL_DOCUMENT]: "Technical Document",
	[AttachmentType.DIAGRAM]: "Diagram or Drawing",
	[AttachmentType.PHOTO]: "Photo",
	[AttachmentType.AUDIO]: "Audio Clip",
	[AttachmentType.GUIDE]: "Guide"
};

export const AttachmentTypePluralizedDisplayNames: Record<AttachmentType, string> = {
	[AttachmentType.MANUAL]: "Manuals",
	[AttachmentType.VIDEO]: "Videos",
	[AttachmentType.GENERAL_DOCUMENT]: "General Documents",
	[AttachmentType.TECHNICAL_DOCUMENT]: "Technical Documents",
	[AttachmentType.DIAGRAM]: "Diagrams or Drawings",
	[AttachmentType.PHOTO]: "Photos",
	[AttachmentType.AUDIO]: "Audio Clips",
	[AttachmentType.GUIDE]: "Guides"
};

export enum PropertyWarrantyStatus {
	Active = "ACTIVE",
	ExpiringSoon = "ENDING",
	Expired = "EXPIRED"
}

export enum InstalledProductWarrantyStatus {
	Active = "ACTIVE",
	ExpiringSoon = "ENDING",
	Expired = "EXPIRED"
}

export enum PropertyDocumentsType {
	// Floor-plan documents feed the Ollie room-hierarchy extraction. The value must
	// stay "FLOOR_PLAN" — the backend filters property_documents on this exact type.
	FLOOR_PLAN = "FLOOR_PLAN",
	DRAWING = "drawing",
	MARKUPS = "Plan Markups",
	DESIGN = "Design",
	OTHER = "Other"
}

export const PropertyDocumentsTypeDisplayNames: Record<PropertyDocumentsType, string> = {
	[PropertyDocumentsType.FLOOR_PLAN]: "Floor Plan",
	[PropertyDocumentsType.DRAWING]: "Diagram or Drawing",
	[PropertyDocumentsType.MARKUPS]: "Plan Markups",
	[PropertyDocumentsType.DESIGN]: "Design",
	[PropertyDocumentsType.OTHER]: "Other"
};

export const warrantyType = "Warranty";

export enum UserInvitationsStatus {
	Accepted = "Accepted",
	Pending = "Pending",
	Canceled = "Canceled"
}

export enum MediaType {
	Image = "image",
	Document = "document",
	Video = "video",
	Audio = "audio",
	PDF = "pdf"
}

export enum OrderStatus {
	pending = "pending",
	confirmed = "confirmed",
	processing = "processing",
	completed = "completed",
	cancelled = "cancelled"
}

export enum AppBrand {
	Highnote = "Highnote",
	MaestroSync = "MaestroSync"
}

export enum UserRoleValue {
	admin_user = "admin_user",
	vendor_manager = "vendor_manager",
	vendor_employee = "vendor_employee",
	customer_manager = "customer_manager",
	customer_employee = "customer_employee",
	authority_manager = "authority_manager", // AHJ manager: everything an authority user can do, plus users and organization admin.
	authority_user = "authority_user", // AHJ staff: inspections, plan reviews, guides and devices for their jurisdiction.
	basic = "basic"
}

// Roles that belong to an Authority (AHJ) organization and are scoped by its jurisdiction.
export const AUTHORITY_USER_ROLES = [UserRoleValue.authority_manager, UserRoleValue.authority_user];

export const isAuthorityRole = (role?: string | null): boolean => AUTHORITY_USER_ROLES.includes(role as UserRoleValue);

// Roles that belong to a Customer organization and only ever operate within it.
export const CUSTOMER_USER_ROLES = [UserRoleValue.customer_manager, UserRoleValue.customer_employee];

export const isCustomerRole = (role?: string | null): boolean => CUSTOMER_USER_ROLES.includes(role as UserRoleValue);

// Roles gated behind the Ollie inspections "Contact Sales" paywall (MWA-836) until
// self-pay billing ships for inspections the way it already has for plan reviews.
export const OLLIE_INSPECTIONS_GATED_ROLES = [
	UserRoleValue.customer_employee,
	UserRoleValue.customer_manager,
	UserRoleValue.vendor_employee,
	UserRoleValue.vendor_manager
];

export const isOllieInspectionsGatedRole = (role?: string | null): boolean =>
	OLLIE_INSPECTIONS_GATED_ROLES.includes(role as UserRoleValue);

// The platform admin. Used where something is not part of anybody's job but ours:
// the screens that load and correct the jurisdiction maps, and the running commentary
// the AHJ lookup writes about how it reached its answer.
export const isPlatformAdminRole = (role?: string | null): boolean => role === UserRoleValue.admin_user;

// Roles that see the reviewer-only parts of a plan review: the original code documents (which can
// carry licensing restrictions) and the reviewer feedback. Customers see the findings without them.
export const canSeeReviewerTools = (role?: string | null): boolean =>
	isAuthorityRole(role) || isPlatformAdminRole(role);

export enum UserRoleInvitationLabel {
	admin_user = "Platform Admin",
	vendor_manager = "Manager",
	vendor_employee = "Contributor",
	customer_manager = "Manager",
	customer_employee = "User",
	authority_manager = "AHJ Manager",
	authority_user = "AHJ Staff"
}

export enum UserRoleLabel {
	admin_user = "Platform Admin",
	vendor_manager = "Vendor Organization Manager",
	vendor_employee = "Vendor Organization Contributor",
	customer_manager = "Customer Organization Manager",
	customer_employee = "Customer Organization User",
	authority_manager = "AHJ Manager",
	authority_user = "AHJ Staff"
}

export enum OrganizationType {
	Vendor = "Vendor",
	Customer = "Customer",
	// Authority Having Jurisdiction. Named for the general case (fire marshal, building
	// department, any enforcing body) rather than "AHJ" so other authorities fit later.
	Authority = "Authority",
	Free = "Free"
}

// Side navigation profile of an organization (organizations.nav_profile).
// When an organization has no value, the role of the user decides.
export enum NavProfile {
	Default = "default",
	Ollie = "ollie"
}

export enum VendorCustomersStatus {
	active = "active",
	inactive = "inactive",
	pending = "pending"
}

export enum NotificationType {
	DEVICE_STATE_CHANGE = "DEVICE_STATE_CHANGE",
	HANDOVER_STATE_CHANGE = "HANDOVER_STATE_CHANGE",
	// Sent to the users of an AHJ when an inspection is Fast Lane or a plan review is APPROVED.
	FAST_LANE_READY = "FAST_LANE_READY"
}

// Per-user delivery cadence for the scheduled (push) Brief emails (MWA-463). Stored on
// users.brief_email_frequency; the daily cron only emails users whose value is not OFF.
export enum BriefEmailFrequency {
	DAILY = "DAILY", // Default. A Brief email is sent on every daily cron run.
	WEEKLY = "WEEKLY", // A Brief email is sent once per week.
	OFF = "OFF" // User opted out; no Brief emails are sent.
}

export enum GuideStatus {
	processing = "processing",
	draft = "draft",
	published = "published",
	failed = "failed",
	deleted = "deleted"
}

export enum InputSuggestionType {
	AI_SUGGESTED_QUERY = "AI_SUGGESTED_QUERY",
	INTELLIGENCE_QUERY = "INTELLIGENCE_QUERY",
	GUIDE_COMMENT = "GUIDE_COMMENT",
	OLLIE_QUERY = "OLLIE_QUERY"
}

// Audience of an AI chat thread. Stored on ai_chats.chat_type and sent to Doctrine as
// `chat_type`; the values must stay lowercase to match the existing rows and the backend default.
export enum AiChatType {
	CLIENT = "client", // End-user assistant (Dashboard and AI Chat routes).
	ADMIN = "admin", // Admin-facing Intelligence assistant over the team's knowledge and feedback.
	OLLIE = "ollie" // Ollie, the assistant of the inspection and plan review screens (/ollie/*).
}

// Where a chat was started from, within its chat type. Stored on ai_chats.chat_origin and sent to the
// backend as `chat_origin`; the values must stay lowercase to match the column default and existing rows.
export enum AiChatOrigin {
	PRIMARY = "primary", // The main conversation of its chat type: the Dashboard / AI Chat one, or Intelligence.
	SPECIALTY = "specialty" // Started with "Ask a question about this" on a device, shop product or guide.
}

// For user_activities table
export enum EventType {
	VIEW = "VIEW",
	INSERT = "INSERT",
	UPDATE = "UPDATE",
	DELETE = "DELETE"
}

export enum EntityType {
	GUIDES = "GUIDES",
	HANDOVER = "HANDOVER"
}

export enum DeviceDetectionJobStatus {
	PENDING = "pending",
	PROCESSING = "processing",
	DRAFT = "draft",
	PUBLISHED = "published",
	FAILED = "failed"
}

export enum HandoverStatus {
	/**
	 * The customer has requested a handover from a vendor.
	 * The ball is in the vendor's court — they must complete the three sections
	 * (Devices & Equipment, How-To Guides, Additional Resources) and submit.
	 *
	 * Entry point for the customer-initiated path.
	 * Triggered by: customer submits the handover request form.
	 * Next state: PENDING_CUSTOMER (when vendor submits).
	 */
	PENDING_VENDOR = "PENDING_VENDOR",

	/**
	 * The vendor has initiated the handover themselves and is still preparing it.
	 * No notification has been sent to the customer yet.
	 * This state allows the vendor to save progress without prematurely alerting the customer.
	 *
	 * Entry point for the vendor-initiated path.
	 * Triggered by: vendor submits the handover creation form.
	 * Next state: PENDING_CUSTOMER (when vendor submits).
	 */
	VENDOR_DRAFT = "VENDOR_DRAFT",

	/**
	 * The vendor has submitted the handover for customer review.
	 * Both paths (customer-initiated and vendor-initiated) converge here.
	 * The ball is in the customer's court — they must review all sections and either
	 * approve (publish) or cancel.
	 *
	 * Triggered by: vendor presses Submit on the Complete Handover screen.
	 * Side effect: customer is notified that the handover is ready for review.
	 * Next state: PUBLISHED or CANCELLED.
	 */
	PENDING_CUSTOMER = "PENDING_CUSTOMER",

	/**
	 * The customer has reviewed and approved the handover.
	 * All associated data (products, installed products, guides, attachments) has been
	 * persisted to the property record. The handover is now read-only for both parties.
	 *
	 * Terminal state (positive).
	 * Triggered by: customer presses Accept and Publish.
	 */
	PUBLISHED = "PUBLISHED",

	/**
	 * The handover was cancelled by either party before reaching PUBLISHED.
	 * Kept as a terminal state to preserve audit history and avoid hard deletes.
	 *
	 * Terminal state (negative).
	 * Note: cancellation actions are out of scope for v1 but the state is defined now
	 * to avoid costly schema and data migrations once handovers exist in production.
	 */
	CANCELLED = "CANCELLED"
}

export enum ProofCenterCategoryKeys {
	ACCESS_CONTROL = "ACCESS_CONTROL",
	SECURITY = "SECURITY",
	FIRE_SAFETY = "FIRE_SAFETY",
	OPERATIONS = "OPERATIONS",
	EMERGENCY = "EMERGENCY",
	WATER = "WATER",
	SURVEILLANCE = "SURVEILLANCE"
}

export enum ProofCenterStatus {
	draft = "draft",
	published = "published"
}

// ─────────────────────────────────────────────────────────────────────────────
// INSPECTION ENUMS — synced from maetro-service/app/constants/field-values.mjs
// ─────────────────────────────────────────────────────────────────────────────

export enum InspectionChecklistItemStatus {
	PENDING = "PENDING", // No evidence submission yet for this checklist item.
	UPLOADING = "UPLOADING", // uploading is frontend-only transient state
	ANALYZING = "ANALYZING", // Evidence submitted; Gemini is analyzing the submission.
	READY = "READY", // Gemini analysis complete; finding_status = PRESENT (item is compliant).
	WARNING = "WARNING", // Gemini found UNCERTAIN or OBSTRUCTED; item needs further review.
	FAIL = "FAIL" // Gemini found MISSING or DAMAGED, or analysis failed; item is non-compliant.
}

export enum InspectionEvidenceFindingStatus {
	PRESENT = "PRESENT", // Item is clearly visible and appears compliant with evaluation criteria (maps to checklist item status READY).
	MISSING = "MISSING", // Item was expected but not found in the submitted evidence (maps to checklist item status FAIL).
	OBSTRUCTED = "OBSTRUCTED", // Item is present but access or visibility is blocked; cannot fully assess compliance (maps to checklist item status WARNING; requires further review).
	DAMAGED = "DAMAGED", // Item is visible but shows signs of damage or non-compliance (maps to checklist item status FAIL).
	UNCERTAIN = "UNCERTAIN" // Cannot determine item status from available evidence; ambiguous or low-confidence observation (maps to checklist item status WARNING; requires further review).
}

// Provisional, collection-time compliance verdict shown per item while the user is still
// collecting evidence. Preliminary and single-item only — the authoritative cross-item
// verdict is InspectionGapAnalysisStatus, produced at submit. Read from
// evidence_findings.provisional_status. UI must label it as preliminary, not final.
export enum InspectionProvisionalVerdictStatus {
	PASS = "PASS", // Evidence clearly satisfies the item's evaluation criteria.
	FAIL = "FAIL", // Evidence shows the requirement is unmet (MISSING/DAMAGED, or a clear violation).
	WARNING = "WARNING", // Borderline / only partially compliant (often OBSTRUCTED); needs another look.
	UNCERTAIN = "UNCERTAIN" // Compliance cannot be judged from the available evidence (ambiguous / low confidence).
}

// Unified inspector feedback. The three-state decision replaces the legacy
// `accurate` boolean; error_attribution splits perception- vs reasoning-model accuracy.
export enum InspectorFeedbackDecision {
	CONFIRMED = "CONFIRMED", // Ollie's call was right; no follow-up.
	PARTIALLY_CORRECT = "PARTIALLY_CORRECT", // Mostly right but something was off; prompts attribution.
	INCORRECT = "INCORRECT" // Ollie's call was wrong; prompts attribution.
}

export enum InspectorFeedbackErrorAttribution {
	PERCEPTION = "PERCEPTION", // Ollie read the scene wrong (the Observation) — links finding_id.
	REASONING = "REASONING", // Ollie applied the code wrong (the Compliance call) — links gap_item_id.
	BOTH = "BOTH" // Both were off — links both.
}

export enum InspectionEvidenceReviewStatus {
	PENDING = "PENDING", // Evidence submission record created; waiting for Gemini analysis to start (initial state on INSERT).
	PROCESSING = "PROCESSING", // Gemini analysis in progress (set by reviewEvidenceSubmission before calling Gemini).
	COMPLETE = "COMPLETE", // Gemini analysis returned a finding; evidence_findings record inserted (set after finding persisted).
	FAILED = "FAILED" // Gemini analysis failed or returned invalid response; no finding record created (error handling in reviewEvidenceSubmission).
}

export enum InspectionJobStatus {
	PENDING_CHECKLIST = "PENDING_CHECKLIST", // Job created, waiting for Doctrine to generate the checklist.
	COLLECTING = "COLLECTING", // Checklist received and loaded; inspector is collecting evidence item by item.
	ANALYZING = "ANALYZING", // All evidence submitted; Doctrine /inspect/analyze is running the gap analysis (job state transitions here when user submits all items or mark job as ready for analysis).
	COMPLETE = "COMPLETE", // Gap analysis finished; readiness_status and iris_score calculated; report ready for inspector to review.
	FAILED = "FAILED" // Unrecoverable error at any step (checklist generation, evidence review, gap analysis).
}

export enum InspectionGapAnalysisStatus {
	PASS = "PASS", // Checklist requirement met (evidence submitted and Gemini found item PRESENT, or requirement satisfied via document review).
	FAIL = "FAIL", // Checklist requirement not met (evidence submitted but Gemini found item MISSING or DAMAGED; or requirement not fulfilled by submitted evidence).
	WARNING = "WARNING", // Requirement has potential issues but not blocking (evidence found UNCERTAIN or OBSTRUCTED; may need on-site verification or clarification).
	NOT_APPLICABLE = "NOT_APPLICABLE", // Requirement does not apply to this property (determined by Doctrine based on property context and jurisdiction).
	UNINSPECTED = "UNINSPECTED" // Requirement is applicable but no evidence was submitted (user skipped this item or collection ended before this item).
}

export enum InspectionGapAnalysisSeverity {
	CRITICAL = "CRITICAL", // Requirement failure that blocks compliance or poses immediate safety risk (must be resolved before occupancy/approval).
	HIGH = "HIGH", // Significant requirement failure that affects safety or code adherence (should be resolved on-site during inspection).
	MEDIUM = "MEDIUM", // Moderate issue; requirement not fully met but does not pose immediate risk (may be resolved via corrective action plan).
	INFO = "INFO" // Informational finding; no non-compliance but worth noting for completeness.
}

// ─────────────────────────────────────────────────────────────────────────────
// PLAN PRE-SCREENING (mirrors the backend PlanReview* enums)
// ─────────────────────────────────────────────────────────────────────────────

export enum PlanReviewJobStatus {
	COLLECTING = "COLLECTING", // Job created; the AHJ user is uploading the package of plans/documents.
	PREPARING = "PREPARING", // Package submitted; oversized PDFs are being downscaled/split so the model can read them.
	ANALYZING = "ANALYZING", // Package ready; Gemini is analyzing the documents against the AHJ requirements.
	COMPLETE = "COMPLETE", // Analysis finished; findings + compliance_score ready to review.
	FAILED = "FAILED" // Unrecoverable error during preparation or analysis.
}

// A rule is either paperwork/completeness or a code the design must satisfy.
export enum PlanReviewRequirementType {
	SUBMITTAL = "SUBMITTAL",
	CODE = "CODE"
}

// Where a rule came from, shown as a badge on the rules screen.
// What a code_books row is. An amendment is one jurisdiction's local changes to a
// foundational code, and it governs wherever it differs.
export enum CodeBookType {
	FOUNDATIONAL = "FOUNDATIONAL",
	AMENDMENT = "AMENDMENT"
}

// How a rule came to cite a code section.
export enum CodeCitationOrigin {
	MANUAL = "MANUAL", // An AHJ manager picked it on the rules screen.
	AI_EXTRACTION = "AI_EXTRACTION" // Resolved from the citation the extraction read.
}

export enum PlanReviewRequirementOrigin {
	SEED = "SEED", // Shipped with the platform.
	MANUAL = "MANUAL", // Written by an AHJ manager.
	AI_EXTRACTION = "AI_EXTRACTION" // Imported from a reviewed extraction.
}

// A run of the AI rule extraction.
export enum PlanReviewExtractionStatus {
	EXTRACTING = "EXTRACTING",
	READY = "READY",
	COMPLETED = "COMPLETED", // Reviewed, with nothing left to import. Set by a database trigger.
	CANCELLED = "CANCELLED", // Discarded by a manager while READY. Readable, but nothing can change.
	FAILED = "FAILED"
}

// How an extracted candidate relates to the rules already on file.
export enum PlanReviewCandidateMatchKind {
	NEW = "NEW",
	IDENTICAL = "IDENTICAL",
	CHANGED = "CHANGED"
}

// What the reviewer decided to do with a candidate.
export enum PlanReviewCandidateResolution {
	IMPORT = "IMPORT",
	SKIP = "SKIP",
	REPLACE = "REPLACE",
	KEEP_BOTH = "KEEP_BOTH"
}

export enum PlanReviewFindingStatus {
	PASS = "PASS", // The requirement is satisfied by the submitted package.
	FAIL = "FAIL", // The requirement is not met (a clear deficiency the reviewer would comment on).
	WARNING = "WARNING", // Potential/borderline issue that likely needs clarification.
	MISSING = "MISSING", // A required submittal (document/field) was not provided in the package.
	NOT_APPLICABLE = "NOT_APPLICABLE" // The requirement does not apply to this project.
}

export enum PlanReviewFindingSeverity {
	CRITICAL = "CRITICAL",
	HIGH = "HIGH",
	MEDIUM = "MEDIUM",
	INFO = "INFO"
}

// Reviewer rating of one plan review finding (plan_review_feedback). A finding carries the
// observation and the compliance call together, so both attributions land on the same row.
export enum PlanReviewFeedbackDecision {
	CONFIRMED = "CONFIRMED", // Ollie's call was right; no follow-up.
	PARTIALLY_CORRECT = "PARTIALLY_CORRECT", // Mostly right but something was off; prompts attribution.
	INCORRECT = "INCORRECT" // Ollie's call was wrong; prompts attribution.
}

export enum PlanReviewFeedbackErrorAttribution {
	PERCEPTION = "PERCEPTION", // Ollie read the plans wrong (the Observation).
	REASONING = "REASONING", // Ollie applied the requirement wrong (the Compliance call).
	BOTH = "BOTH" // Both were off.
}

// Rollup status for a Review Category (the category badge in the report).
export enum PlanReviewCategoryStatus {
	COMPLIANT = "COMPLIANT",
	REVISIONS_NEEDED = "REVISIONS_NEEDED",
	MISSING_CONTENT = "MISSING_CONTENT"
}

// Overall readiness verdict for the submittal — the header status badge.
export enum PlanReviewReviewOutcome {
	APPROVED = "APPROVED",
	REVISIONS_NEEDED = "REVISIONS_NEEDED"
}

// ─────────────────────────────────────────────────────────────────────────────
// AHJ RESOLUTION (MWA-696) — mirrors maetro-service/app/constants/field-values.mjs
// ─────────────────────────────────────────────────────────────────────────────

// What kind of work an area has authority over. Stored on jurisdiction_types.domain
// and on plan_review_submission_types.jurisdiction_domain, and what ties the two
// together: a submittal filed under `fire` resolves against a fire area.
export enum JurisdictionDomain {
	FIRE = "fire",
	BUILDING = "building",
	ZONING = "zoning",
	FLOODPLAIN = "floodplain",
	// Areas that are context, not authority: a city, a county, an ETJ.
	ADMINISTRATIVE = "administrative"
}

// Which desk inside a discipline answers. Stored on
// jurisdiction_boundary_authorities.work_kind and on
// plan_review_submission_types.jurisdiction_work_kind. A published fire-marshal
// layer answers "who has authority here" differently for a burn permit than for a
// building permit over the very same polygon, which is what this distinguishes.
export enum JurisdictionWorkKind {
	// The area's own authority: what answers where a layer names only one.
	GENERAL = "general",
	CONSTRUCTION_PERMIT = "construction_permit",
	SITE_REVIEW = "site_review",
	OPERATIONAL_INSPECTION = "operational_inspection",
	BURN_PERMIT = "burn_permit",
	FIREWORKS = "fireworks",
	MOBILE_FOOD = "mobile_food",
	TEMPORARY_EVENT = "temporary_event"
}

// How the AHJ lookup arrived at its answer, reported on resolve_jurisdiction. Both
// values are derived from data that can be re-checked; there is deliberately no
// value for "a person picked this", and nothing about a lookup is stored.
export enum JurisdictionMatchMethod {
	BOUNDARY = "boundary", // The property's coordinates fall inside an ingested polygon.
	ADMINISTRATIVE = "administrative" // No polygons there yet; matched on the geocoded city or county.
}

// How much the answer can be trusted. Reported on resolve_jurisdiction, and stored
// on boundary_authority_proposals.confidence, where it is the model's own rating.
export enum JurisdictionConfidence {
	HIGH = "high",
	MEDIUM = "medium",
	LOW = "low"
}

// Where a proposed link between an ingested boundary and an AHJ stands. Stored on
// boundary_authority_proposals.status.
export enum BoundaryAuthorityProposalStatus {
	PENDING = "PENDING",
	APPROVED = "APPROVED",
	REJECTED = "REJECTED"
}

// Whether the county the layer publishes for an area agrees with the county the
// proposed authority is filed under. Stored on boundary_authority_proposals.county_check.
export enum JurisdictionCountyCheck {
	MATCH = "MATCH",
	MISMATCH = "MISMATCH", // A reason to look, not a verdict: authorities do serve across county lines.
	UNKNOWN = "UNKNOWN" // The layer publishes no county to compare against.
}

// How precisely the geocoder placed an address, as Google reports it. Anything below
// ROOFTOP was inferred rather than looked up, which is what new construction gets.
export enum GeocodeLocationType {
	ROOFTOP = "ROOFTOP",
	RANGE_INTERPOLATED = "RANGE_INTERPOLATED",
	GEOMETRIC_CENTER = "GEOMETRIC_CENTER",
	APPROXIMATE = "APPROXIMATE"
}

// Where a run of importing a boundary layer stands. Stored on
// jurisdiction_boundary_imports.status.
export enum JurisdictionBoundaryImportStatus {
	DOWNLOADING = "DOWNLOADING", // Fetching the layer, which on a county-wide layer takes minutes.
	IMPORTING = "IMPORTING", // Walking its features into jurisdiction_boundaries.
	COMPLETE = "COMPLETE",
	FAILED = "FAILED"
}

// AI vendor that runs a feature. Stored on ai_model_configs.provider.
export enum AiProvider {
	Gemini = "gemini",
	Claude = "claude"
}

// Where a request to claim a jurisdiction from the /claim screen stands. Stored on
// authority_claims.status.
export enum AuthorityClaimStatus {
	PENDING_VERIFICATION = "PENDING_VERIFICATION", // The email link was sent and not opened yet.
	PENDING_REVIEW = "PENDING_REVIEW", // The email is verified, and an admin has to approve the jurisdiction.
	APPROVED = "APPROVED", // An admin approved it. The account is created when the person opens the new email link.
	REJECTED = "REJECTED", // An admin rejected it. No account is created.
	COMPLETED = "COMPLETED" // The account exists and belongs to the Authority organization of the jurisdiction.
}

// How the jurisdiction of a claim was found. Stored on authority_claims.match_source.
export enum AuthorityClaimMatchSource {
	CONTACT_LIST = "CONTACT_LIST", // The email is in jurisdiction_contacts.
	EMAIL_DOMAIN = "EMAIL_DOMAIN", // The email domain is in jurisdiction_email_domains.
	MANUAL_SELECTION = "MANUAL_SELECTION", // The person picked the jurisdiction from the list. Needs a review.
	ADMIN = "ADMIN", // An admin created the claim from the claims screen.
	NEW_JURISDICTION = "NEW_JURISDICTION" // The person proposed a jurisdiction that is not in the list. Needs a review.
}
